import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const ROUND_LABELS = {
  R256: "Turno Prel.", R128: "128-esimi", R64: "64-esimi", R32: "32-esimi",
  R16: "Sedicesimi", QF: "Quarti", SF: "Semifinale", F: "Finale",
  QU: "Qualificazione", PI: "Play-In",
};

/* ─── Compute standard round name for QU rounds based on match count ─── */
function matchCountToRoundInfo(count) {
  if (count >= 64) return { code: "R128", name: "128-esimi" };
  if (count >= 32) return { code: "R64", name: "64-esimi" };
  if (count >= 16) return { code: "R32", name: "32-esimi" };
  if (count >= 8) return { code: "R16", name: "Sedicesimi" };
  if (count >= 4) return { code: "QF", name: "Quarti" };
  if (count >= 2) return { code: "SF", name: "Semifinale" };
  return { code: "F", name: "Finale" };
}
function computeDisplayLabel(roundCode, positionInRound) {
  if (roundCode === "F") return "F";
  if (roundCode === "SF") return `SF${positionInRound + 1}`;
  if (roundCode === "QF") return `QF${positionInRound + 1}`;
  return `${roundCode}-${positionInRound + 1}`;
}

const DEPTH_NAMES = ["Finale", "Semifinale", "Quarti", "Ottavi", "Sedicesimi", "32-esimi", "64-esimi", "128-esimi", "256-esimi"];
const DEPTH_CODES = ["F", "SF", "QF", "R8", "R16", "R32", "R64", "R128", "R256"];

function computeGlobalRoundInfo(matches) {
  const feeders = {};
  for (const m of matches) {
    if (m.next_match_id) {
      if (!feeders[m.next_match_id]) feeders[m.next_match_id] = [];
      feeders[m.next_match_id].push(m.match_id);
    }
  }
  let finals = matches.filter(m => !m.next_match_id && m.round === "F");
  if (finals.length === 0) finals = matches.filter(m => !m.next_match_id);
  const depth = {};
  const queue = [];
  for (const f of finals) { depth[f.match_id] = 0; queue.push({ id: f.match_id, d: 0 }); }
  while (queue.length > 0) {
    const { id, d } = queue.shift();
    for (const fid of (feeders[id] || [])) {
      if (depth[fid] === undefined) {
        depth[fid] = d + 1;
        queue.push({ id: fid, d: d + 1 });
      }
    }
  }
  const sriDepth = {};
  for (const m of matches) {
    const key = `${m.section}|${m.round_index ?? 0}`;
    const d = depth[m.match_id];
    if (d !== undefined) {
      if (sriDepth[key] === undefined || d === sriDepth[key]) sriDepth[key] = d;
      else sriDepth[key] = Math.max(sriDepth[key], d);
    }
  }
  const roundNameMap = {};
  for (const [key, d] of Object.entries(sriDepth)) {
    roundNameMap[key] = DEPTH_NAMES[d] || `${Math.pow(2, d)}-esimi`;
  }
  const labelOverrides = {};
  for (const m of matches) {
    const d = depth[m.match_id];
    if (d !== undefined) {
      const code = DEPTH_CODES[d] || `R${Math.pow(2, d)}`;
      if (code === "F") labelOverrides[m.match_id] = "F";
      else if (code === "SF") labelOverrides[m.match_id] = `SF${(m.position_in_round ?? 0) + 1}`;
      else if (code === "QF") labelOverrides[m.match_id] = `QF${(m.position_in_round ?? 0) + 1}`;
      else labelOverrides[m.match_id] = `${code}-${(m.position_in_round ?? 0) + 1}`;
    }
  }
  return { roundNameMap, labelOverrides };
}

const isQualPlaceholder = (playerId) => typeof playerId === "string" && playerId.startsWith("QUAL_");

async function loadImageAsBase64(url) {
  try {
    const res = await fetch(url, { credentials: "include" });
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

async function addVenueHeader(doc, venue, yStart, topRightLabel) {
  let y = yStart;
  const pageW = doc.internal.pageSize.getWidth();
  const marginL = 14;
  let textX = marginL;

  // Resolve main logo URL: tournament-venue has `logo_path` (proxied),
  // user-venue has `logo_url` (direct or relative).
  let mainLogoUrl = "";
  if (venue.logo_path) {
    mainLogoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${venue.logo_path}`;
  } else if (venue.logo_url) {
    mainLogoUrl = venue.logo_url;
  }

  if (mainLogoUrl) {
    try {
      const base64 = await loadImageAsBase64(mainLogoUrl);
      if (base64) {
        doc.addImage(base64, "PNG", marginL, y, 20, 20);
        textX = marginL + 24;
      }
    } catch { /* skip logo */ }
  }

  // Build full address: "address, ZIP city (province)"
  const fullAddressParts = [];
  if (venue.address) fullAddressParts.push(venue.address);
  const locality = [venue.zip, venue.city].filter(Boolean).join(" ").trim();
  if (locality) fullAddressParts.push(locality);
  if (venue.province) fullAddressParts.push(`(${venue.province})`);
  const fullAddress = fullAddressParts.join(" ");

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(venue.name || "", textX, y + 6);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 100, 100);

  let lineY = y + 12;
  if (fullAddress) { doc.text(fullAddress, textX, lineY); lineY += 5; }
  const contactBits = [];
  if (venue.phone) contactBits.push(`Tel: ${venue.phone}`);
  if (venue.email) contactBits.push(venue.email);
  if (contactBits.length) { doc.text(contactBits.join("  •  "), textX, lineY); lineY += 5; }

  // Maps link
  const mapsLink = venue.maps_link || venue.google_maps_link;
  if (mapsLink) {
    doc.setFontSize(8);
    doc.setTextColor(0, 100, 0);
    const linkText = "Indicazioni stradali";
    const linkW = doc.getTextWidth(linkText);
    const linkX = textX;
    doc.textWithLink(linkText, linkX, lineY, { url: mapsLink });
    doc.setDrawColor(0, 100, 0);
    doc.line(linkX, lineY + 1, linkX + linkW, lineY + 1);
    lineY += 5;
  }

  doc.setTextColor(0, 0, 0);

  // Extra logos (sponsor / federation / partners) — top-right corner
  const extraLogos = Array.isArray(venue.extra_logos) ? venue.extra_logos.filter(Boolean) : [];
  let extraBottomY = y;
  if (extraLogos.length > 0) {
    const logoH = 14;
    const logoW = 14;
    const gap = 3;
    const totalW = extraLogos.length * (logoW + gap) - gap;
    let x = pageW - marginL - totalW;
    const logoY = y;
    for (const url of extraLogos) {
      try {
        const b64 = await loadImageAsBase64(url);
        if (b64) doc.addImage(b64, "PNG", x, logoY, logoW, logoH);
      } catch { /* skip broken logo */ }
      x += logoW + gap;
    }
    extraBottomY = logoY + logoH;
  }

  // Top-right label (e.g. "Tabellone generato secondo Regolamento FITP")
  // placed BELOW extra logos but ABOVE the separator line.
  let labelY = extraBottomY;
  if (topRightLabel) {
    doc.setFontSize(6.5);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(0, 90, 160);
    const ly = extraLogos.length > 0 ? extraBottomY + 3 : y + 4;
    doc.text(topRightLabel, pageW - marginL, ly, { align: "right" });
    labelY = ly;
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
  }

  y = Math.max(y + 22, lineY, labelY + 2);
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(marginL, y, pageW - marginL, y);
  return y + 4;
}

function addPageHeader(doc, venue, y) {
  const pageW = doc.internal.pageSize.getWidth();
  const marginL = 14;
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(100, 100, 100);
  doc.text(venue.name || "", marginL, y);
  if (venue.phone) {
    doc.text(`Tel: ${venue.phone}`, pageW - marginL - doc.getTextWidth(`Tel: ${venue.phone}`), y);
  }
  doc.setTextColor(0, 0, 0);
  doc.setDrawColor(200, 200, 200);
  doc.line(marginL, y + 2, pageW - marginL, y + 2);
  return y + 6;
}

/* ─── Draw a bracket for one section (March 30 original style) ─── */
function drawBracketSection(doc, activeRounds, filteredRounds, seedMap, startY, marginL, marginR, pageW, pageH, roundDisplayMap, labelOverrides, matchSectionMap) {
  const cardW = 38;
  const cardH = 13;
  const colGap = 14;
  const numRounds = activeRounds.length;

  const availW = pageW - marginL - marginR;
  const availH = pageH - startY - 6;
  const firstRoundCount = filteredRounds[activeRounds[0]]?.length || 1;
  const minGapFirstRound = 3;
  const neededH = firstRoundCount * cardH + (firstRoundCount - 1) * minGapFirstRound;

  const scale = Math.min(availW / (numRounds * cardW + (numRounds - 1) * colGap), availH / neededH, 1);
  const sCardW = cardW * scale;
  const sCardH = cardH * scale;
  const sColGap = colGap * scale;

  // Build feeder map using next_match_id
  const cardPositions = {};
  const roundCardPositions = {};
  const matchById = {};
  for (const r of activeRounds) {
    for (const m of (filteredRounds[r] || [])) {
      matchById[m.match_id] = m;
    }
  }

  for (let rIdx = 0; rIdx < activeRounds.length; rIdx++) {
    const roundKey = activeRounds[rIdx];
    const roundMatches = filteredRounds[roundKey] || [];
    if (roundMatches.length === 0) continue;

    const colX = marginL + rIdx * (sCardW + sColGap);
    roundCardPositions[roundKey] = [];

    if (rIdx === 0) {
      const compactGap = 3 * scale;

      for (let mIdx = 0; mIdx < roundMatches.length; mIdx++) {
        const m = roundMatches[mIdx];
        const cardY = startY + mIdx * (sCardH + compactGap);
        const pos = { x: colX, y: cardY, w: sCardW, h: sCardH };
        cardPositions[m.match_id] = pos;
        roundCardPositions[roundKey].push({ match: m, ...pos });
      }
    } else {
      // Find feeders using next_match_id chain
      const feederMap = {};
      for (const prevKey of activeRounds.slice(0, rIdx)) {
        for (const card of (roundCardPositions[prevKey] || [])) {
          const nid = card.match.next_match_id;
          if (nid) {
            if (!feederMap[nid]) feederMap[nid] = [];
            feederMap[nid].push(card);
          }
        }
      }

      for (let mIdx = 0; mIdx < roundMatches.length; mIdx++) {
        const m = roundMatches[mIdx];
        const feeders = (feederMap[m.match_id] || []).sort((a, b) => a.y - b.y);

        let cardY;
        if (feeders.length >= 2) {
          cardY = (feeders[0].y + feeders[0].h / 2 + feeders[1].y + feeders[1].h / 2) / 2 - sCardH / 2;
        } else if (feeders.length === 1) {
          cardY = feeders[0].y + feeders[0].h / 2 - sCardH / 2;
        } else {
          const compactGap = 3 * scale;
          cardY = startY + mIdx * (sCardH + compactGap);
        }

        const pos = { x: colX, y: cardY, w: sCardW, h: sCardH };
        cardPositions[m.match_id] = pos;
        roundCardPositions[roundKey].push({ match: m, ...pos });
      }
    }
  }

  // STEP 2: Connector lines (behind cards)
  doc.setDrawColor(160, 175, 190);
  doc.setLineWidth(0.3);

  const allCards = [];
  for (const r of activeRounds) {
    for (const c of (roundCardPositions[r] || [])) allCards.push(c);
  }

  for (const card of allCards) {
    const nextId = card.match.next_match_id;
    if (!nextId || !cardPositions[nextId]) continue;
    // Skip connector lines FROM bye matches (they have no visible box)
    const isByeCard = card.match.status === "bye" || card.match.player1_id === "BYE" || card.match.player2_id === "BYE";
    if (isByeCard) continue;
    const nextPos = cardPositions[nextId];

    const siblings = allCards.filter(c => c.match.next_match_id === nextId && c.match.match_id !== card.match.match_id);
    if (siblings.length > 0) {
      const sibling = siblings[0];
      if (card.y > sibling.y) continue;

      const rightX = card.x + card.w;
      const leftX = nextPos.x;
      const midX = rightX + (leftX - rightX) / 2;
      const y1 = card.y + card.h / 2;
      const y2 = sibling.y + sibling.h / 2;
      const yMid = nextPos.y + nextPos.h / 2;

      doc.line(rightX, y1, midX, y1);
      doc.line(rightX, y2, midX, y2);
      doc.line(midX, y1, midX, y2);
      doc.line(midX, yMid, leftX, yMid);
    } else {
      doc.line(card.x + card.w, card.y + card.h / 2, nextPos.x, nextPos.y + nextPos.h / 2);
    }
  }

  // STEP 3: Draw match cards ON TOP of lines
  for (let rIdx = 0; rIdx < activeRounds.length; rIdx++) {
    const roundKey = activeRounds[rIdx];
    const cards = roundCardPositions[roundKey] || [];
    if (cards.length === 0) continue;

    // Round header
    const firstCard = cards[0];
    doc.setFontSize(Math.max(5, 7 * scale));
    doc.setFont("helvetica", "bold");
    doc.setTextColor(34, 85, 34);
    const roundLabel = (roundDisplayMap && roundDisplayMap[roundKey]) || ROUND_LABELS[roundKey.replace(/_\d+$/, '')] || roundKey.replace(/_\d+$/, '');
    const lblW = doc.getTextWidth(roundLabel);
    doc.text(roundLabel, firstCard.x + (sCardW - lblW) / 2, firstCard.y - 2 * scale);
    doc.setTextColor(0, 0, 0);

    for (const { match: m, x: colX, y: cardY } of cards) {
      const isBye = m.status === "bye";
      const isSingleBye = !isBye && (m.player1_id === "BYE" || m.player2_id === "BYE");

      // BYE matches: draw a light gray ghost box instead of hiding completely
      if (isBye || isSingleBye) {
        doc.setFillColor(226, 232, 240);
        doc.setDrawColor(203, 213, 225);
        doc.setLineWidth(0.2 * scale);
        doc.roundedRect(colX, cardY, sCardW, sCardH, 0.5, 0.5, "FD");
        // Gray text
        doc.setFontSize(Math.max(3, 4 * scale));
        doc.setFont("helvetica", "italic");
        doc.setTextColor(148, 163, 184);
        const byeName = m.player1_id === 'BYE' ? (m.player2_name || 'BYE') : (m.player1_name || 'BYE');
        const byeText = `${byeName} — BYE`;
        const tw = doc.getTextWidth(byeText);
        doc.text(byeText, colX + (sCardW - tw) / 2, cardY + sCardH / 2 + 1);
        continue;
      }

      const isCompleted = m.status === "completed";

      // Card background
      if (isCompleted) {
        doc.setFillColor(240, 253, 244);
        doc.setDrawColor(22, 163, 74);
      } else if (isBye) {
        doc.setFillColor(248, 250, 252);
        doc.setDrawColor(203, 213, 225);
      } else {
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(203, 213, 225);
      }
      doc.setLineWidth(0.3 * scale);
      doc.roundedRect(colX, cardY, sCardW, sCardH, 0.5, 0.5, "FD");

      // Match label header inside box
      if (m.match_label) {
        const labelH = 3.5 * scale;
        doc.setFillColor(241, 245, 249);
        doc.rect(colX, cardY, sCardW, labelH, "F");
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.15);
        doc.line(colX, cardY + labelH, colX + sCardW, cardY + labelH);

        doc.setFontSize(Math.max(4, 5.5 * scale));
        doc.setFont("helvetica", "bold");
        doc.setTextColor(100, 116, 139);
        const lbl = (labelOverrides && labelOverrides[m.match_id]) || m.match_label;
        doc.text(lbl, colX + sCardW / 2 - doc.getTextWidth(lbl) / 2, cardY + labelH - 0.8 * scale);
      }

      const contentY = cardY + 3.5 * scale;
      const rowH = (sCardH - 3.5 * scale) / 2;

      // Player 1
      const p1Seed = m.player1_seed || (m.player1_id ? (seedMap || {})[m.player1_id] : null);
      drawPlayerRow(doc, m.player1_name, m.player1_id, m.winner_id, colX, contentY, rowH, sCardW, scale, p1Seed, m.player1_ranking, m.score);

      // Divider
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.15);
      doc.line(colX + 0.5, contentY + rowH, colX + sCardW - 0.5, contentY + rowH);

      // Player 2
      const p2Id = m.player2_id || (isBye && !m.player2_id ? "BYE" : m.player2_id);
      const p2Seed = m.player2_seed || (m.player2_id ? (seedMap || {})[m.player2_id] : null);
      drawPlayerRow(doc, m.player2_name, p2Id, m.winner_id, colX, contentY + rowH, rowH, sCardW, scale, p2Seed, m.player2_ranking, m.score);

      // Schedule info (Campo, Orario, Data DD/MM/YY, durata) — positioned BELOW the box
      if ((m.scheduled_time || m.duration_minutes) && !isBye) {
        const infoY = cardY + sCardH + 2.5 * scale;
        doc.setFontSize(Math.max(3, 4.5 * scale));
        doc.setFont("helvetica", "normal");
        doc.setTextColor(80, 80, 80);
        const parts = [];
        if (m.scheduled_court != null) parts.push(`C${m.scheduled_court}`);
        if (m.scheduled_time) parts.push(m.scheduled_time);
        if (m.scheduled_date) {
          const d = m.scheduled_date.split('-');
          if (d.length === 3) parts.push(`${d[2]}/${d[1]}/${d[0].slice(2)}`);
        }
        if (m.duration_minutes) parts.push(`${m.duration_minutes}'`);
        const info = parts.join(' ');
        const infoW = doc.getTextWidth(info);
        doc.text(info, colX + (sCardW - infoW) / 2, infoY);
      }

      // Cross-section qualifier label (line + green text with Q{n} format)
      if (matchSectionMap && m.next_match_id && matchSectionMap[m.next_match_id] && matchSectionMap[m.next_match_id] !== m.section) {
        const lineStartX = colX + sCardW + 0.5;
        const lineY = cardY + sCardH / 2;
        const lineEndX = lineStartX + 4;
        doc.setDrawColor(100, 116, 139);
        doc.setLineWidth(0.3);
        doc.line(lineStartX, lineY, lineEndX, lineY);
        doc.setFontSize(Math.max(3, 4 * scale));
        doc.setFont("helvetica", "bold");
        doc.setTextColor(22, 128, 61);
        const qualLabel = `Q${(m.position_in_round ?? 0) + 1}`;
        doc.text(`${qualLabel} Sez.${m.section}`, lineEndX + 0.5, lineY + 1);
      }
    }
  }
}

function drawPlayerRow(doc, playerName, playerId, winnerId, colX, rowY, rowH, sCardW, scale, seedNum, ranking, score) {
  const name = playerName || (playerId === "BYE" ? "BYE" : "TBD");
  const isWinner = winnerId && winnerId === playerId;
  const isBye = playerId === "BYE";
  const isQual = isQualPlaceholder(playerId);

  if (isWinner) {
    doc.setFillColor(220, 252, 231);
    doc.rect(colX + 0.3, rowY, sCardW - 0.6, rowH, "F");
  }

  doc.setFontSize(Math.max(3.5, 5 * scale));
  if (isBye) {
    doc.setFont("helvetica", "italic");
    doc.setTextColor(160, 160, 160);
  } else if (isQual) {
    doc.setFont("helvetica", "italic");
    doc.setTextColor(180, 120, 0);
  } else if (isWinner) {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(22, 101, 52);
  } else {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(30, 41, 59);
  }

  let text = name.length > 20 ? name.substring(0, 19) + "." : name;

  // Append ranking
  if (ranking && playerId && !isBye && !isQual) {
    text += ` ${ranking}`;
  }
  // Append seed
  if (seedNum) {
    text += ` (${seedNum})`;
  }
  // Winner mark + score (only on the winning row)
  if (isWinner) {
    text += " (W)";
    if (score) text += ` ${score}`;
  }

  if (text.length > 36) text = text.substring(0, 35) + ".";
  doc.text(text, colX + 1.5 * scale, rowY + rowH / 2 + 0.8 * scale);
}

/* ─── Build round groups from matches using round_index ─── */
function buildRoundGroups(sectionMatches) {
  const byRoundIdx = {};
  for (const m of sectionMatches) {
    const ri = m.round_index ?? 0;
    if (!byRoundIdx[ri]) byRoundIdx[ri] = [];
    byRoundIdx[ri].push(m);
  }
  const sortedIdxs = Object.keys(byRoundIdx).map(Number).sort((a, b) => a - b);
  const activeRounds = [];
  const filteredRounds = {};
  for (const ri of sortedIdxs) {
    const roundMatches = byRoundIdx[ri];
    const roundName = roundMatches[0]?.round || `R${ri}`;
    const roundKey = `${roundName}_${ri}`;
    activeRounds.push(roundKey);
    filteredRounds[roundKey] = roundMatches
      .filter(m => !(m.player1_id === "BYE" && m.player2_id === "BYE"))
      .sort((a, b) => a.position_in_round - b.position_in_round);
  }

  // Compute standard round display names for QU rounds
  const firstRoundCount = filteredRounds[activeRounds[0]]?.length || 1;
  const roundDisplayMap = {};
  const labelOverrides = {};
  activeRounds.forEach((roundKey, rIdx) => {
    const origRound = roundKey.replace(/_\d+$/, '');
    if (origRound !== "QU") {
      roundDisplayMap[roundKey] = ROUND_LABELS[origRound] || origRound;
      return;
    }
    const expectedCount = Math.max(1, Math.round(firstRoundCount / Math.pow(2, rIdx)));
    const info = matchCountToRoundInfo(expectedCount);
    roundDisplayMap[roundKey] = info.name;
    for (const m of (filteredRounds[roundKey] || [])) {
      labelOverrides[m.match_id] = computeDisplayLabel(info.code, m.position_in_round);
    }
  });

  return { activeRounds, filteredRounds, roundDisplayMap, labelOverrides };
}

function addNotesFooter(doc, notes) {
  if (!notes || !notes.trim()) return;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginL = 14;
  const marginR = 14;
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    const maxW = pageW - marginL - marginR;
    const lines = doc.splitTextToSize(notes, maxW);
    const lineH = 3.2;
    const block = Math.min(lines.length, 4); // hard limit 4 rows ≈ 12.8mm
    const startY = pageH - 5 - (block - 1) * lineH;
    // Separator above notes
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    doc.line(marginL, startY - 2, pageW - marginR, startY - 2);
    // Label
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    doc.setTextColor(120, 120, 120);
    doc.text("NOTE:", marginL, startY - 0.2);
    // Notes body
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(60, 60, 60);
    const textStartX = marginL + 8.5;
    for (let j = 0; j < block; j++) {
      doc.text(lines[j], textStartX, startY + j * lineH);
    }
  }
  doc.setTextColor(0, 0, 0);
}

/* ─── MAIN: Export Bracket PDF ─── */
export async function exportBracketPDF(tournament, drawData, categoryName, venue, notes) {
  const matches = drawData?.matches || [];
  if (matches.length === 0) return;

  const marginL = 10;
  const marginR = 10;
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Build seed map
  const seedMap = {};
  for (const m of matches) {
    if (m.player1_seed && m.player1_id) seedMap[m.player1_id] = m.player1_seed;
    if (m.player2_seed && m.player2_id) seedMap[m.player2_id] = m.player2_seed;
  }

  // Compute global round names (distance from Final across all sections)
  const globalRoundInfo = computeGlobalRoundInfo(matches);

  // Build matchSectionMap for cross-section qualifier labels
  const matchSectionMap = {};
  for (const m of matches) {
    matchSectionMap[m.match_id] = m.section;
  }

  // Multi-section detection
  const sections = [...new Set(matches.map(m => m.section).filter(Boolean))];
  const SECTION_ORDER = ["4.NC", "4.x", "3.x", "2.x", "1.x"];
  sections.sort((a, b) => {
    const ia = SECTION_ORDER.indexOf(a);
    const ib = SECTION_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });

  if (sections.length > 1) {
    // Multi-section: each section on its own page
    for (let sIdx = 0; sIdx < sections.length; sIdx++) {
      if (sIdx > 0) doc.addPage("landscape");
      const sectionName = sections[sIdx];
      const sectionMatches = matches.filter(m => m.section === sectionName);

      let startY = 8;
      if (venue) startY = await addVenueHeader(doc, venue, startY, "Tabellone generato secondo Regolamento FITP");

      // Title
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(`${tournament.name} - ${categoryName}`, marginL, startY + 5);
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(180, 120, 0);
      doc.text(`Sezione ${sectionName}`, marginL, startY + 10);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      startY += 14;

      const { activeRounds, filteredRounds, roundDisplayMap, labelOverrides } = buildRoundGroups(sectionMatches);
      // Apply global round names (distance from Final)
      for (const roundKey of activeRounds) {
        const rMatches = filteredRounds[roundKey] || [];
        if (rMatches.length > 0) {
          const m0 = rMatches[0];
          const gKey = `${m0.section}|${m0.round_index ?? 0}`;
          if (globalRoundInfo.roundNameMap[gKey]) roundDisplayMap[roundKey] = globalRoundInfo.roundNameMap[gKey];
        }
        for (const m of rMatches) {
          if (globalRoundInfo.labelOverrides[m.match_id]) labelOverrides[m.match_id] = globalRoundInfo.labelOverrides[m.match_id];
        }
      }
      if (activeRounds.length === 0) continue;

      drawBracketSection(doc, activeRounds, filteredRounds, seedMap, startY, marginL, marginR, pageW, pageH, roundDisplayMap, labelOverrides, matchSectionMap);
    }
  } else {
    // Single section
    let startY = 8;
    if (venue) startY = await addVenueHeader(doc, venue, startY, "Tabellone generato secondo Regolamento FITP");

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(`${tournament.name} - ${categoryName}`, marginL, startY + 5);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text("Tabellone", marginL, startY + 9);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    startY += 14;

    const { activeRounds, filteredRounds, roundDisplayMap, labelOverrides } = buildRoundGroups(matches);
    // Apply global round names (distance from Final)
    for (const roundKey of activeRounds) {
      const rMatches = filteredRounds[roundKey] || [];
      if (rMatches.length > 0) {
        const m0 = rMatches[0];
        const gKey = `${m0.section}|${m0.round_index ?? 0}`;
        if (globalRoundInfo.roundNameMap[gKey]) roundDisplayMap[roundKey] = globalRoundInfo.roundNameMap[gKey];
      }
      for (const m of rMatches) {
        if (globalRoundInfo.labelOverrides[m.match_id]) labelOverrides[m.match_id] = globalRoundInfo.labelOverrides[m.match_id];
      }
    }
    if (activeRounds.length === 0) return;

    drawBracketSection(doc, activeRounds, filteredRounds, seedMap, startY, marginL, marginR, pageW, pageH, roundDisplayMap, labelOverrides, matchSectionMap);
  }

  // Add notes footer to every page
  addNotesFooter(doc, notes);

  doc.save(`${tournament.name}_${categoryName}_Tabellone.pdf`);
}

/* ─── Export Schedule PDF ─── */
export async function exportSchedulePDF(tournament, schedule, venue, drawMatches, notes) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const marginL = 14;

  let y = 10;
  if (venue) y = await addVenueHeader(doc, venue, y);

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text(tournament.name, marginL, y + 5);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 100, 100);
  doc.text("Calendario Partite", marginL, y + 10);
  doc.setTextColor(0, 0, 0);
  y += 15;

  const byDate = schedule?.by_date || {};
  const dates = Object.keys(byDate).filter(d => d && d !== "unscheduled" && d !== "null").sort();

  // Compute global label overrides per category for schedule PDF using full draw
  const allScheduleMatches = (drawMatches && drawMatches.length > 0) ? drawMatches : (schedule?.matches || []);
  const byCat = {};
  for (const m of allScheduleMatches) {
    const cid = m.category_id || "_";
    if (!byCat[cid]) byCat[cid] = [];
    byCat[cid].push(m);
  }
  const scheduleLabelOverrides = {};
  for (const catMatches of Object.values(byCat)) {
    const overrides = computeGlobalRoundInfo(catMatches).labelOverrides;
    Object.assign(scheduleLabelOverrides, overrides);
  }

  for (let di = 0; di < dates.length; di++) {
    const date = dates[di];
    const dayMatches = byDate[date]
      .filter(m => m.status !== "bye" && m.player1_id !== "BYE" && m.player2_id !== "BYE")
      .sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));

    if (di > 0) {
      const remaining = doc.internal.pageSize.getHeight() - y;
      if (remaining < 30) {
        doc.addPage();
        y = 10;
        if (venue) y = addPageHeader(doc, venue, y);
      }
    }

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(34, 85, 34);
    doc.text(date, marginL, y);
    doc.setTextColor(0, 0, 0);
    y += 2;

    const tableData = dayMatches.map(m => [
      m.scheduled_time || "-",
      `C${m.scheduled_court || "-"}`,
      scheduleLabelOverrides[m.match_id] || m.match_label || "-",
      m.category_name || "-",
      ROUND_LABELS[m.round] || m.round,
      m.player1_name || "TBD",
      "vs",
      m.player2_name || "TBD",
      m.score || "-",
      m.duration_minutes ? `${m.duration_minutes}'` : "-",
    ]);

    autoTable(doc, {
      startY: y,
      head: [["Ora", "Campo", "Rif.", "Categoria", "Turno", "Giocatore 1", "", "Giocatore 2", "Punteggio", "Durata"]],
      body: tableData,
      styles: { fontSize: 7, cellPadding: 1.5, font: "helvetica" },
      headStyles: { fillColor: [34, 85, 34], textColor: 255, fontStyle: "bold", fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 14, fontStyle: "bold" },
        1: { cellWidth: 12 },
        2: { cellWidth: 14, fontStyle: "bold" },
        3: { cellWidth: 22 },
        4: { cellWidth: 16 },
        5: { cellWidth: 28 },
        6: { cellWidth: 7, halign: "center" },
        7: { cellWidth: 28 },
        8: { cellWidth: 18 },
        9: { cellWidth: 14, halign: "center" },
      },
      alternateRowStyles: { fillColor: [245, 250, 245] },
      margin: { left: marginL, right: marginL },
      didDrawPage: (data) => {
        if (data.pageNumber > 1 && venue) {
          addPageHeader(doc, venue, 8);
        }
      },
    });

    y = (doc.lastAutoTable?.finalY || y + 20) + 6;
  }

  // Add notes footer to every page
  addNotesFooter(doc, notes);

  doc.save(`${tournament.name}_Calendario.pdf`);
}

/* ─── Helper: court display name for PDF ─── */
function getCourtDisplayNamePDF(courtNames, courtNum, dateStr) {
  if (courtNum == null) return `Campo ${courtNum}`;
  const num = String(courtNum);
  if (courtNames && dateStr && courtNames[dateStr]) {
    const name = courtNames[dateStr][num] || courtNames[dateStr][courtNum];
    if (name && name.trim()) return name.trim();
  }
  if (courtNames) {
    for (const dayNames of Object.values(courtNames)) {
      if (typeof dayNames === "object" && dayNames) {
        const name = dayNames[num] || dayNames[courtNum];
        if (name && name.trim()) return name.trim();
      }
    }
  }
  return `Campo ${courtNum}`;
}

/* ─── Export Official FITP Schedule PDF (Ordine del Giorno) ─── */
export async function exportFITPSchedulePDF(tournament, schedule, venue, drawMatches, notes) {
  const byDate = schedule?.by_date || {};
  const dates = Object.keys(byDate).filter(d => d && d !== "unscheduled" && d !== "null").sort();
  if (dates.length === 0) return;

  const DAYS_IT = ["domenica", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato"];
  const MONTHS_IT = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

  // Global label overrides
  const allScheduleMatches = (drawMatches && drawMatches.length > 0) ? drawMatches : (schedule?.matches || []);
  const byCat = {};
  for (const m of allScheduleMatches) {
    const cid = m.category_id || "_";
    if (!byCat[cid]) byCat[cid] = [];
    byCat[cid].push(m);
  }
  const scheduleLabelOverrides = {};
  for (const catMatches of Object.values(byCat)) {
    const overrides = computeGlobalRoundInfo(catMatches).labelOverrides;
    Object.assign(scheduleLabelOverrides, overrides);
  }

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginL = 14;
  const marginR = 14;

  // Load venue logo once
  let venueLogoBase64 = null;
  if (venue?.logo_path) {
    const logoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${venue.logo_path}`;
    venueLogoBase64 = await loadImageAsBase64(logoUrl);
  }

  let firstPage = true;
  for (let di = 0; di < dates.length; di++) {
    const date = dates[di];
    const dayMatches = (byDate[date] || [])
      .filter(m => m.status !== "bye" && m.player1_id !== "BYE" && m.player2_id !== "BYE")
      .sort((a, b) => {
        const courtA = parseInt(a.scheduled_court) || 0;
        const courtB = parseInt(b.scheduled_court) || 0;
        if (courtA !== courtB) return courtA - courtB;
        return (a.scheduled_time || "").localeCompare(b.scheduled_time || "");
      });

    if (dayMatches.length === 0) continue;
    if (!firstPage) doc.addPage("portrait");
    firstPage = false;

    // ── HEADER ──
    const headerTop = 8;

    // Left: Venue logo
    if (venueLogoBase64) {
      doc.addImage(venueLogoBase64, "PNG", marginL, headerTop, 22, 22);
    }

    // Center: Tournament name
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    const tName = (tournament.name || "").toUpperCase();
    const tNameLines = doc.splitTextToSize(tName, pageW - 80);
    doc.text(tNameLines, pageW / 2, headerTop + 7, { align: "center" });

    // Center: Venue name
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const vName = (venue?.name || "").toUpperCase();
    if (vName) doc.text(vName, pageW / 2, headerTop + 7 + tNameLines.length * 5.5, { align: "center" });

    // Right: FITP badge
    const fitpX = pageW - marginR - 30;
    doc.setFillColor(0, 61, 165);
    doc.rect(fitpX, headerTop, 30, 11, "F");
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("FITP", fitpX + 15, headerTop + 8, { align: "center" });
    doc.setFontSize(5.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 61, 165);
    doc.text("FEDERAZIONE ITALIANA", fitpX + 15, headerTop + 14, { align: "center" });
    doc.text("TENNIS PADEL", fitpX + 15, headerTop + 17.5, { align: "center" });
    doc.setTextColor(0, 0, 0);

    // Date title
    const dateObj = new Date(date + "T12:00:00");
    const dayName = DAYS_IT[dateObj.getDay()];
    const dayNum = dateObj.getDate();
    const monthName = MONTHS_IT[dateObj.getMonth()];
    const year = dateObj.getFullYear();
    const dateLabel = `Orario di ${dayName} ${dayNum} ${monthName} ${year}`;
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(dateLabel, pageW / 2, 33, { align: "center" });

    // Header separator
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.8);
    doc.line(marginL, 36, pageW - marginR, 36);

    // ── TABLE ──
    const courts = [...new Set(dayMatches.map(m => m.scheduled_court).filter(c => c != null))]
      .sort((a, b) => parseInt(a) - parseInt(b));

    const matchesByCourt = {};
    for (const court of courts) {
      matchesByCourt[court] = dayMatches
        .filter(m => String(m.scheduled_court) === String(court))
        .sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
    }

    const maxSlots = Math.max(0, ...courts.map(c => matchesByCourt[c].length));
    const courtHeaders = courts.map(c => getCourtDisplayNamePDF(tournament.court_names, c, date).toUpperCase());
    const tableHead = [["N.", ...courtHeaders]];
    const tableBody = [];

    // Build structured cell data map for custom rendering in didDrawCell
    const cellMatchDataMap = {};

    for (let slot = 0; slot < maxSlots; slot++) {
      const row = [String(slot + 1)];
      for (let courtIdx = 0; courtIdx < courts.length; courtIdx++) {
        const court = courts[courtIdx];
        const match = matchesByCourt[court]?.[slot];
        if (!match) {
          row.push("");
        } else {
          const time = match.scheduled_time || "";
          const timeLabel = slot === 0 ? `Inizio ore: ${time}` : `Non prima delle: ${time}`;
          const p1 = match.player1_name || match.player1_tbd_label || "TBD";
          const p2 = match.player2_name || match.player2_tbd_label || "TBD";
          const catName = match.category_name || "";
          const roundLabel = ROUND_LABELS[match.round] || match.round || "";
          const matchLbl = scheduleLabelOverrides[match.match_id] || match.match_label || "";
          const roundLine = [roundLabel, matchLbl ? `(${matchLbl})` : ""].filter(Boolean).join(" ");
          const score = match.score || "";
          const duration = match.duration_minutes ? `${match.duration_minutes}'` : "";
          const scoreDurationLine = [score, duration].filter(Boolean).join("  ");
          cellMatchDataMap[`${slot},${courtIdx + 1}`] = { timeLabel, catName, roundLine, p1, p2, scoreDurationLine };
          row.push(""); // empty – content rendered by didDrawCell
        }
      }
      tableBody.push(row);
    }

    const contentW = pageW - marginL - marginR;
    const noColW = 10;
    const courtColW = (contentW - noColW) / Math.max(courts.length, 1);
    const columnStyles = {
      0: { cellWidth: noColW, halign: "center", valign: "middle", fontStyle: "bold" },
    };
    courts.forEach((_, idx) => {
      columnStyles[idx + 1] = { cellWidth: courtColW, valign: "top" };
    });

    autoTable(doc, {
      startY: 38,
      head: tableHead,
      body: tableBody,
      styles: {
        fontSize: 8,
        cellPadding: 3,
        font: "helvetica",
        overflow: "linebreak",
        valign: "top",
        minCellHeight: 32,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 9,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        halign: "center",
      },
      bodyStyles: { lineWidth: 0.2, lineColor: [160, 160, 160] },
      columnStyles,
      tableLineWidth: 0.5,
      tableLineColor: [0, 0, 0],
      margin: { left: marginL, right: marginR },
      didDrawCell: (data) => {
        if (data.section !== "body") return;
        if (data.column.index === 0) return;

        const key = `${data.row.index},${data.column.index}`;
        const cd = cellMatchDataMap[key];
        if (!cd) return;

        const { timeLabel, catName, roundLine, p1, p2, scoreDurationLine } = cd;
        const cell = data.cell;
        const cx = cell.x + cell.width / 2;
        const maxW = cell.width - 6;
        let cy = cell.y + 5;
        const lineH = 4.2;

        // 1. Orario — grassetto, centrato
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        doc.text(timeLabel, cx, cy, { align: "center" });
        cy += lineH;

        // 2. Categoria — riga singola, font dinamico, centrata
        if (catName) {
          doc.setFont("helvetica", "normal");
          let catFs = 7.5;
          doc.setFontSize(catFs);
          let catText = catName;
          while (catFs > 4 && doc.getTextWidth(catText) > maxW) {
            catFs -= 0.5;
            doc.setFontSize(catFs);
          }
          while (doc.getTextWidth(catText) > maxW && catText.length > 4) {
            catText = catText.slice(0, -4) + "...";
          }
          doc.setTextColor(40, 40, 40);
          doc.text(catText, cx, cy, { align: "center" });
          cy += lineH - 0.5;
        }

        // 3. Turno — corsivo, piccolo, centrato
        if (roundLine) {
          doc.setFont("helvetica", "italic");
          doc.setFontSize(6.5);
          doc.setTextColor(100, 100, 100);
          let rl = roundLine;
          while (doc.getTextWidth(rl) > maxW && rl.length > 4) {
            rl = rl.slice(0, -4) + "...";
          }
          doc.text(rl, cx, cy, { align: "center" });
          cy += lineH - 0.5;
        }

        // 4. Giocatore 1 — normale, centrato
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        let p1t = p1;
        while (doc.getTextWidth(p1t) > maxW && p1t.length > 4) {
          p1t = p1t.slice(0, -4) + "...";
        }
        doc.text(p1t, cx, cy, { align: "center" });
        cy += lineH;

        // 5. VS — grassetto, centrato
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.text("VS", cx, cy, { align: "center" });
        cy += lineH;

        // 6. Giocatore 2 — normale, centrato
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        let p2t = p2;
        while (doc.getTextWidth(p2t) > maxW && p2t.length > 4) {
          p2t = p2t.slice(0, -4) + "...";
        }
        doc.text(p2t, cx, cy, { align: "center" });
        cy += lineH;

        // 7. Punteggio + Durata — corsivo, piccolo, centrato (solo se presenti)
        if (scoreDurationLine) {
          doc.setFont("helvetica", "italic");
          doc.setFontSize(7);
          doc.setTextColor(60, 90, 60);
          let sd = scoreDurationLine;
          while (doc.getTextWidth(sd) > maxW && sd.length > 4) {
            sd = sd.slice(0, -4) + "...";
          }
          doc.text(sd, cx, cy, { align: "center" });
        }
      },
    });

    // ── FOOTER ──
    const footerTop = pageH - 20;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.line(marginL, footerTop, pageW - marginR, footerTop);

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    const footerTextY = footerTop + 5;
    if (tournament.match_director) {
      doc.text(`Direttore di gara: ${tournament.match_director}`, marginL, footerTextY);
    }
    if (tournament.chief_umpire) {
      doc.text(`Giudice arbitro: ${tournament.chief_umpire}`, marginL, footerTextY + 5);
    }
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = now.getFullYear();
    const hh = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    doc.setFont("helvetica", "italic");
    doc.text(`OdG pubblicato il: ${dd}/${mm}/${yyyy} ${hh}:${min}`, pageW - marginR, footerTextY + 5, { align: "right" });
  }

  const safeName = (tournament.name || "Torneo").replace(/[^a-zA-Z0-9_ \-]/g, "");
  // Add notes footer to every page
  addNotesFooter(doc, notes);
  doc.save(`${safeName}_OrdineDelGiorno.pdf`);
}
